import type { CrmIntegrationProvider } from "app/pages/sales-intelligence/domain/crm-integration/types/provider";
import CRMGatewayButton from "app/pages/sales-intelligence/modules/manage-integrations/components/CRMGatewayButton/CRMGatewayButton";
import GetApiKeyButton from "app/pages/sales-intelligence/modules/manage-integrations/components/GetApiKeyButton/GetApiKeyButton";
import type { ApiPageType } from "app/pages/sales-intelligence/modules/manage-integrations/types/api-page-type";

import {
    renderConnectGAIllustration,
    renderDCWalletIllustration,
    renderSignalsRestApiCreditsIllustration,
    renderSignalsRestApiInfoIllustration,
} from "../components/GatewayPage/GatewayPageIllustrations";
import { GATEWAY_BREADCRUMB_PARENT } from "../constants/gateway-breadcrumb-parent";
import type { IGatewayPageContent } from "../containers/GatewayPageContainer/types";

const KEY_FEATURES_TITLE = "si.manage-integrations.gateway.common.key-features-title";
const DATA_CREDIT_PRICES_TITLE = "si.manage-integrations.gateway.common.data-credit-prices-title";

export function createGatewayPageItem(props: Partial<IGatewayPageContent>): IGatewayPageContent {
    return {
        breadcrumbParent: GATEWAY_BREADCRUMB_PARENT.default,
        titleKey: "",
        descriptionKey: "",
        infoTitleKey: KEY_FEATURES_TITLE,
        infoItemsKeys: [],
        creditsTitleKey: DATA_CREDIT_PRICES_TITLE,
        creditsItemsKeys: [],
        renderInfoIllustration: renderConnectGAIllustration,
        renderCreditsIllustration: renderDCWalletIllustration,
        ...props,
    };
}

export function createGatewayAPIPageItemWithButton(
    apiType: ApiPageType,
    props: Partial<Omit<IGatewayPageContent, "renderActionButton">>,
): IGatewayPageContent {
    return createGatewayPageItem({
        ...props,
        renderActionButton: () => <GetApiKeyButton apiType={apiType} />,
    });
}

export function createGatewaySignalsRestApiPageItem(
    apiType: ApiPageType,
    props: Partial<Omit<IGatewayPageContent, "renderInfoIllustration" | "renderCreditsIllustration">>,
): IGatewayPageContent {
    const { renderStickyActionButton, renderActionButton, ...pageProps } = props;

    return createGatewayPageItem({
        ...pageProps,
        renderInfoIllustration: renderSignalsRestApiInfoIllustration,
        renderCreditsIllustration: renderSignalsRestApiCreditsIllustration,
        renderActionButton:
            renderActionButton ??
            (() => (
                <GetApiKeyButton
                    apiType={apiType}
                    variant={renderStickyActionButton ? "gateway" : "default"}
                    dataAutomation="signals-rest-api.gateway-get-api-key-button"
                />
            )),
        ...(renderStickyActionButton ? { renderStickyActionButton } : {}),
    });
}

export function createGatewayPageItemWithCRMConnect(
    integrationName: CrmIntegrationProvider,
    props: Partial<Omit<IGatewayPageContent, "renderActionButton">>,
): IGatewayPageContent {
    return createGatewayPageItem({
        ...props,
        renderActionButton: () => <CRMGatewayButton integrationName={integrationName} />,
    });
}
